import {
  Claim,
  ClaimStatus,
  CommissionSettlement,
  Company,
  DataField,
  Installment,
  LineOfBusiness,
  OfferStatus,
  Person,
  Policy,
  PolicyOrigin,
  ProtoDb,
  ProtoNotification,
  Quote,
  QuoteOffer,
  QuoteStatus,
  Ticket,
  TicketKind,
  TicketStatus,
  TreeInfo,
  TreeMember,
} from '../models/domain.models';
import { COVERAGE_LEVELS } from '../models/coverage';
import { initialVersion, LINE_PREFIX } from './policy-helpers';
import { addDays, Rng } from './rng';
import { enrichCommissions } from './seed-commissions';
import { enrichLifecycle } from './seed-lifecycle';
import { COMPANIES, COMPANY, MEMBER, MEMBER_FED, MEMBERS, MEMBERS_FED, TREES } from './seed-named';
import { enrichPlatform } from './seed-platform';

const SEED = 20261006;
const SEED_FEDERAL = 20261107;

/** Everything that differs between trees; Ríos Seguros reproduces the original volumes exactly. */
interface TreeSeedSpec {
  treeInfo: TreeInfo;
  /** Inserted in every generated oid (empty for the first tree, so its ids never change). */
  idp: string;
  code: string;
  seed: number;
  members: TreeMember[];
  rootOid: string;
  assignable: string[];
  ownerWeights: [string, number][];
  persons: { total: number; clients: number; named: boolean };
  policies: {
    total: number;
    origins: [number, number, number, number];
    flagged: [number, number, number, number, number, number];
  };
  quoteStatuses: QuoteStatus[];
  claims: { total: number; open: number; near: number };
  settlementMonths: number;
  tickets: { sales: number; endorsements: number; cancellations: number; free: number };
}

function repeat<T>(item: T, n: number): T[] {
  return Array<T>(n).fill(item);
}

const FIRST_NAMES = [
  'Lucía', 'Martina', 'Camila', 'Julieta', 'Agustina', 'Florencia', 'Carolina', 'Romina', 'Paula', 'Natalia',
  'Mateo', 'Santiago', 'Nicolás', 'Facundo', 'Ignacio', 'Gonzalo', 'Federico', 'Pablo', 'Sebastián', 'Hernán',
];
const LAST_NAMES = [
  'Fernández', 'Rodríguez', 'López', 'Martínez', 'Pérez', 'Sánchez', 'Romero', 'Díaz', 'Álvarez', 'Torres',
  'Ruiz', 'Acosta', 'Medina', 'Herrera', 'Suárez', 'Aguirre', 'Molina', 'Castro', 'Ortiz', 'Silva',
];
const BUSINESS_NAMES = [
  'Ferretería El Tornillo SRL', 'Distribuidora Del Sur SA', 'Estudio Contable Núñez', 'Transportes Pampeanos SRL',
  'Kiosco Central SAS', 'Clínica Santa Rosa SA', 'Taller Mecánico Ruiz', 'Librería Del Centro SRL',
  'Panadería Los Álamos SRL', 'Constructora Horizonte SA', 'Agroinsumos Litoral SA',
];
const STREETS = ['Av. Rivadavia', 'Belgrano', 'San Martín', 'Mitre', 'Sarmiento', 'Av. Corrientes', 'Moreno', 'Lavalle'];
const CARS = [
  ['Toyota', 'Corolla'], ['Volkswagen', 'Gol Trend'], ['Ford', 'Ranger'], ['Chevrolet', 'Cruze'],
  ['Fiat', 'Cronos'], ['Renault', 'Kwid'], ['Peugeot', '208'], ['Honda', 'HR-V'],
];
const CLAIM_TYPES = ['Choque', 'Robo total', 'Robo parcial', 'Granizo', 'Daños por agua', 'Incendio', 'Responsabilidad civil a terceros'];
const PAYMENT_METHODS = ['Tarjeta de crédito', 'Débito automático (CBU)', 'Cupón de pago'];

const LINE_WEIGHTS: [LineOfBusiness, number][] = [
  ['auto', 55], ['home', 20], ['commerce', 8], ['liability', 6], ['personal-accident', 6], ['life', 5],
];
export const PREMIUM_RANGE: Record<LineOfBusiness, [number, number]> = {
  auto: [300000, 900000], home: [80000, 200000], commerce: [150000, 500000],
  liability: [100000, 350000], 'personal-accident': [40000, 120000], life: [60000, 250000],
};
const OWNER_WEIGHTS: [string, number][] = [
  [MEMBER.martin, 38], [MEMBER.laura, 14], [MEMBER.sofia, 14], [MEMBER.tomas, 12],
  [MEMBER.gestion, 10], [MEMBER.valentina, 8], [MEMBER.diego, 4],
];
const FEDERAL_OWNER_WEIGHTS: [string, number][] = [
  [MEMBER_FED.ferro, 30], [MEMBER_FED.martin, 30], [MEMBER_FED.claudia, 25], [MEMBER_FED.pablo, 15],
];

const RIOS_SPEC: TreeSeedSpec = {
  treeInfo: TREES[0], idp: '', code: '', seed: SEED, members: MEMBERS, rootOid: MEMBER.martin,
  assignable: [MEMBER.martin, MEMBER.martin, MEMBER.martin, MEMBER.sofia, MEMBER.sofia, MEMBER.laura, MEMBER.tomas, MEMBER.gestion],
  ownerWeights: OWNER_WEIGHTS,
  persons: { total: 150, clients: 110, named: true },
  policies: { total: 320, origins: [128, 112, 48, 32], flagged: [15, 4, 20, 20, 6, 18] },
  quoteStatuses: [
    ...repeat<QuoteStatus>('draft', 5), ...repeat<QuoteStatus>('quoting', 3), ...repeat<QuoteStatus>('presented', 12),
    ...repeat<QuoteStatus>('chosen-by-client', 4), ...repeat<QuoteStatus>('accepted', 5), ...repeat<QuoteStatus>('issued', 10),
    ...repeat<QuoteStatus>('desisted', 6),
  ],
  claims: { total: 18, open: 10, near: 2 },
  settlementMonths: 12,
  tickets: { sales: 6, endorsements: 4, cancellations: 2, free: 8 },
};

const FEDERAL_SPEC: TreeSeedSpec = {
  treeInfo: TREES[1], idp: 'f-', code: 'F', seed: SEED_FEDERAL, members: MEMBERS_FED, rootOid: MEMBER_FED.ferro,
  assignable: [MEMBER_FED.ferro, MEMBER_FED.ferro, MEMBER_FED.martin, MEMBER_FED.martin, MEMBER_FED.claudia, MEMBER_FED.pablo],
  ownerWeights: FEDERAL_OWNER_WEIGHTS,
  persons: { total: 30, clients: 22, named: false },
  policies: { total: 64, origins: [26, 22, 10, 6], flagged: [3, 1, 4, 4, 1, 4] },
  quoteStatuses: [
    ...repeat<QuoteStatus>('draft', 1), ...repeat<QuoteStatus>('quoting', 1), ...repeat<QuoteStatus>('presented', 2),
    ...repeat<QuoteStatus>('chosen-by-client', 1), ...repeat<QuoteStatus>('accepted', 1), ...repeat<QuoteStatus>('issued', 3),
    ...repeat<QuoteStatus>('desisted', 1),
  ],
  claims: { total: 4, open: 3, near: 1 },
  settlementMonths: 3,
  tickets: { sales: 2, endorsements: 1, cancellations: 1, free: 3 },
};

function pad(n: number, size: number): string {
  return String(n).padStart(size, '0');
}

function weighted<T>(rng: Rng, entries: [T, number][]): T {
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  let roll = rng.next() * total;
  for (const [item, weight] of entries) {
    roll -= weight;
    if (roll < 0) return item;
  }
  return entries[entries.length - 1][0];
}

function known<T>(value: T): DataField<T> {
  return { state: 'known', value };
}

function plate(rng: Rng): string {
  const letters = 'ABCDEFGHJKLMNPRSTVWXYZ';
  const l = () => letters[rng.int(0, letters.length - 1)];
  return `${l()}${l()}${pad(rng.int(0, 999), 3)}${l()}${l()}`;
}

function compatibleCompanies(companies: Company[], line: LineOfBusiness): Company[] {
  return companies.filter(c => c.lines.includes(line));
}

function makePersons(rng: Rng, today: string, spec: TreeSeedSpec): Person[] {
  const persons: Person[] = [];
  const named: [string, 'natural' | 'legal', string][] = [
    ['Carlos Benítez', 'natural', '20345678'],
    ['Marta Giménez', 'natural', '24567890'],
    ['Ricardo Domínguez', 'natural', '18901234'],
    ['Panadería La Espiga SRL', 'legal', '30712345678'],
  ];
  (spec.persons.named ? named : []).forEach(([displayName, legalForm, documentNumber], i) => {
    persons.push({
      oid: `per-${spec.idp}${pad(i + 1, 4)}`, kind: 'client', legalForm, displayName,
      documentType: legalForm === 'legal' ? 'CUIT' : 'DNI', documentNumber,
      email: known(`cliente${i + 1}@correo.example`), phone: known(`11 4444-${pad(100 + i, 4)}`),
      address: known(`${rng.pick(STREETS)} ${rng.int(100, 4800)}, CABA`),
      ownerOid: spec.rootOid, createDate: addDays(today, -rng.int(100, 700)),
    });
  });
  const total = spec.persons.total;
  const clientsTarget = spec.persons.clients;
  for (let i = persons.length; i < total; i++) {
    const legal = rng.chance(0.08);
    const displayName = legal
      ? rng.pick(BUSINESS_NAMES)
      : `${rng.pick(FIRST_NAMES)} ${rng.pick(LAST_NAMES)}`;
    const kind = i < clientsTarget ? 'client' : 'lead';
    const phoneState = rng.next();
    const emailState = rng.next();
    persons.push({
      oid: `per-${spec.idp}${pad(i + 1, 4)}`, kind, legalForm: legal ? 'legal' : 'natural', displayName,
      documentType: legal ? 'CUIT' : 'DNI',
      documentNumber: legal ? `30${rng.int(10000000, 99999999)}${rng.int(0, 9)}` : String(rng.int(14000000, 45000000)),
      email: emailState < 0.85
        ? known(`${displayName.toLowerCase().replace(/[^a-z]+/g, '.').replace(/^\.|\.$/g, '')}@correo.example`)
        : { state: 'unreported' },
      phone: phoneState < 0.8 ? known(`11 ${rng.int(2000, 6999)}-${pad(rng.int(0, 9999), 4)}`) : { state: phoneState < 0.92 ? 'pending' : 'unreported' },
      address: kind === 'lead' ? { state: 'pending' } : known(`${rng.pick(STREETS)} ${rng.int(100, 4800)}, ${rng.pick(['CABA', 'La Plata', 'Rosario', 'Córdoba'])}`),
      ownerOid: weighted(rng, spec.ownerWeights),
      createDate: addDays(today, -rng.int(5, 700)),
    });
  }
  return persons;
}

function policySubject(rng: Rng, line: LineOfBusiness): string {
  switch (line) {
    case 'auto': {
      const [brand, model] = rng.pick(CARS);
      return `${brand} ${model} ${rng.int(2008, 2025)} (${plate(rng)})`;
    }
    case 'home':
      return `Vivienda en ${rng.pick(STREETS)} ${rng.int(100, 4800)}`;
    case 'commerce':
      return `Local comercial en ${rng.pick(STREETS)} ${rng.int(100, 4800)}`;
    case 'liability':
      return 'Responsabilidad civil general';
    case 'personal-accident':
      return 'Accidentes personales individual';
    case 'life':
      return 'Vida individual enlatado';
  }
}

function makePolicies(rng: Rng, today: string, persons: Person[], spec: TreeSeedSpec): Policy[] {
  const clients = persons.filter(p => p.kind === 'client');
  const total = spec.policies.total;
  const [platform, file, api, manual] = spec.policies.origins;
  const origins: PolicyOrigin[] = [
    ...Array<PolicyOrigin>(platform).fill('platform'),
    ...Array<PolicyOrigin>(file).fill('file-import'),
    ...Array<PolicyOrigin>(api).fill('api-import'),
    ...Array<PolicyOrigin>(manual).fill('manual'),
  ];
  const shuffledOrigins = rng.shuffle(origins);
  const indexes = rng.shuffle(Array.from({ length: total }, (_, i) => i));
  const cuts = spec.policies.flagged.reduce<number[]>((acc, n) => [...acc, (acc[acc.length - 1] ?? 0) + n], []);
  const [cRenewing, cDivergent, cStale, cExpired, cCancelled, cNoInstallments] = cuts;
  const renewing = new Set(indexes.slice(0, cRenewing));
  const divergent = new Set(indexes.slice(cRenewing, cDivergent));
  const stale = new Set(indexes.slice(cDivergent, cStale));
  const expired = new Set(indexes.slice(cStale, cExpired));
  const cancelled = new Set(indexes.slice(cExpired, cCancelled));
  const noInstallments = new Set(indexes.slice(cCancelled, cNoInstallments));

  const policies: Policy[] = [];
  for (let i = 0; i < total; i++) {
    const line = weighted(rng, LINE_WEIGHTS);
    const person = rng.pick(clients);
    const company = divergent.has(i)
      ? COMPANIES.find(c => c.oid === COMPANY.pampa)!
      : rng.pick(compatibleCompanies(COMPANIES, line));
    let startDate: string;
    let status: Policy['status'] = 'active';
    if (renewing.has(i)) {
      startDate = addDays(today, rng.int(1, 30) - 365);
    } else if (expired.has(i)) {
      startDate = addDays(today, -rng.int(400, 700));
      status = 'expired';
    } else {
      startDate = addDays(today, -rng.int(5, 330));
    }
    if (cancelled.has(i)) status = 'cancelled';
    const [min, max] = PREMIUM_RANGE[line];
    const number = `${LINE_PREFIX[line]}-${pad(rng.int(0, 999999), 6)}`;
    const premium = Math.round(rng.int(min, max) / 100) * 100;
    const subject = policySubject(rng, line);
    policies.push({
      oid: `pol-${spec.idp}${pad(i + 1, 4)}`,
      number,
      lineOfBusiness: line,
      companyOid: company.oid,
      personOid: person.oid,
      ownerOid: person.ownerOid,
      origin: shuffledOrigins[i],
      status,
      startDate,
      endDate: addDays(startDate, 365),
      premium,
      subject,
      lastSyncDate: stale.has(i) ? addDays(today, -rng.int(31, 90)) : addDays(today, -rng.int(0, 6)),
      divergent: divergent.has(i),
      renewalOfOid: null,
      installmentsLoaded: !noInstallments.has(i),
      versions: [initialVersion(startDate, premium, subject)],
    });
  }
  return policies;
}

function makeInstallments(rng: Rng, today: string, policies: Policy[]): Installment[] {
  const out: Installment[] = [];
  for (const policy of policies) {
    if (!policy.installmentsLoaded || policy.status !== 'active') continue;
    const count = rng.chance(0.3) ? 12 : 6;
    const stepDays = Math.round(365 / count);
    for (let n = 1; n <= count; n++) {
      const dueDate = addDays(policy.startDate, (n - 1) * stepDays);
      const past = dueDate < today;
      const recent = past && dueDate >= addDays(today, -45);
      const overdue = recent && rng.chance(0.05);
      const methodRoll = rng.next();
      const paymentMethod: DataField<string> =
        methodRoll < 0.7 ? known(rng.pick(PAYMENT_METHODS))
          : methodRoll < 0.85 ? { state: 'unreported' }
            : methodRoll < 0.95 ? { state: 'pending' }
              : { state: 'not-applicable' };
      out.push({
        oid: `ins-${policy.oid.slice(4)}-${pad(n, 2)}`,
        policyOid: policy.oid, ownerOid: policy.ownerOid, number: n, dueDate,
        amount: Math.round(policy.premium / count),
        status: overdue ? 'overdue' : past ? 'paid' : 'pending',
        paidDate: past && !overdue ? addDays(dueDate, rng.int(0, 4)) : null,
        paymentMethod,
      });
    }
  }
  return out;
}

function makeQuotes(rng: Rng, today: string, persons: Person[], spec: TreeSeedSpec): Quote[] {
  const statuses = spec.quoteStatuses;
  const quotes: Quote[] = [];
  statuses.forEach((status, i) => {
    const line = weighted(rng, LINE_WEIGHTS);
    const person = rng.pick(persons);
    const candidates = rng.shuffle(compatibleCompanies(COMPANIES, line)).slice(0, rng.int(3, 5));
    const levels = COVERAGE_LEVELS[line];
    const offers: QuoteOffer[] = status === 'draft' ? [] : candidates.map(c => {
      let offerStatus: OfferStatus = 'received';
      if (status === 'quoting') offerStatus = rng.chance(0.5) ? 'pending' : 'received';
      else if (c.oid === COMPANY.nexo && rng.chance(0.3)) offerStatus = 'no-offer';
      else if (c.oid === COMPANY.federal && rng.chance(0.3)) offerStatus = 'timeout';
      const [min, max] = PREMIUM_RANGE[line];
      return {
        companyOid: c.oid, status: offerStatus, attempt: 1,
        premium: offerStatus === 'received' ? Math.round(rng.int(min, max) / 100) * 100 : null,
        coverageLevel: offerStatus === 'received' ? rng.pick(levels) : null,
        deductible: offerStatus === 'received' && line === 'auto' ? rng.pick([0, 50000, 100000]) : null,
        respondedAt: offerStatus === 'pending' ? null : addDays(today, -rng.int(0, 30)),
        message: offerStatus === 'no-offer' ? 'Sin oferta para este riesgo' : offerStatus === 'timeout' ? 'La compañía no respondió a tiempo' : null,
      };
    });
    const risk: Record<string, string | number> = line === 'auto'
      ? { vehicle: `${rng.pick(CARS).join(' ')}`, vehicleYear: rng.int(2005, 2025) }
      : { description: policySubject(rng, line) };
    quotes.push({
      oid: `quo-${spec.idp}${pad(i + 1, 4)}`, number: `COT-${spec.code}${pad(i + 1, 5)}`, lineOfBusiness: line,
      personOid: person.oid, ownerOid: person.ownerOid, status,
      createDate: addDays(today, -rng.int(0, 60)), risk, offers,
      version: 1, previousVersions: [],
      chosenCompanyOid: ['chosen-by-client', 'accepted', 'issued'].includes(status)
        ? offers.find(o => o.status === 'received')?.companyOid ?? null : null,
      share: null, clientChoice: null, desistReason: null,
    });
  });
  return quotes;
}

function makeClaims(rng: Rng, today: string, policies: Policy[], spec: TreeSeedSpec): Claim[] {
  const active = policies.filter(p => p.status === 'active' && p.lineOfBusiness !== 'life');
  const claims: Claim[] = [];
  const openStatuses: ClaimStatus[] = ['reported', 'documentation', 'in-analysis'];
  const closedStatuses: ClaimStatus[] = ['accepted', 'rejected', 'closed'];
  for (let i = 0; i < spec.claims.total; i++) {
    const policy = rng.pick(active);
    const open = i < spec.claims.open;
    const nearDeadline = i < spec.claims.near;
    const reportedDaysAgo = nearDeadline ? 25 + i : open ? rng.int(2, 20) : rng.int(35, 160);
    const reportedDate = addDays(today, -reportedDaysAgo);
    claims.push({
      oid: `clm-${spec.idp}${pad(i + 1, 3)}`, number: `SIN-${pad(rng.int(10000, 99999), 5)}`,
      policyOid: policy.oid, personOid: policy.personOid, companyOid: policy.companyOid, ownerOid: policy.ownerOid,
      status: open ? rng.pick(openStatuses) : rng.pick(closedStatuses),
      incidentDate: addDays(reportedDate, -rng.int(0, 5)), reportedDate,
      type: rng.pick(CLAIM_TYPES), description: 'Siniestro de demostración con descripción ilustrativa.',
      legalDeadlineDate: addDays(reportedDate, 30),
      companyClaimNumber: `CIA-${pad(i * 7919 % 90000 + 10000, 5)}`,
      lastUpdateDate: addDays(reportedDate, Math.min(reportedDaysAgo, (i * 3) % 9)),
      settledAmount: !open && i % 3 !== 0 ? Math.round(policy.premium * 0.4 / 100) * 100 : null,
      route: 'manual', incidentTime: null, location: '', affectedItem: policy.subject, injured: false, policeReport: false,
      thirdParties: [], checklist: [], history: [], lastQueryDate: null,
    });
  }
  claims.filter(c => c.status === 'rejected').forEach(c => { c.settledAmount = null; });
  return claims;
}

function makeSettlements(
  rng: Rng, today: string, policies: Policy[], spec: TreeSeedSpec,
): { settlements: CommissionSettlement[]; claimSettlementOids: string[] } {
  const settlements: CommissionSettlement[] = [];
  const claimSettlementOids: string[] = [];
  const base = new Date(`${today}T12:00:00Z`);
  const austral = policies.filter(p => p.companyOid === COMPANY.austral);
  rng.shuffle(austral);
  const diffMonths = [1, 3, 5];
  for (let m = spec.settlementMonths; m >= 1; m--) {
    const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() - m, 1));
    const period = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1, 2)}`;
    for (const company of COMPANIES) {
      rng.int(150000, 600000);
      const oid = `set-${spec.idp}${company.oid.slice(4)}-${period}`;
      if (company.oid === COMPANY.austral && diffMonths.includes(m) && claimSettlementOids.length < 3) claimSettlementOids.push(oid);
      settlements.push({
        oid, ownerOid: spec.rootOid, companyOid: company.oid, period, receivedDate: today, via: 'file', fileName: null,
        settledAmount: 0, unmatched: 0, hasDetail: true,
      });
    }
  }
  return { settlements, claimSettlementOids };
}

function makeTickets(rng: Rng, today: string, db: ProtoDb, claimSettlementOids: string[], spec: TreeSeedSpec): Ticket[] {
  const tickets: Ticket[] = [];
  const assignable = spec.assignable;
  const statuses: TicketStatus[] = ['open', 'in-progress', 'waiting-company', 'resolved', 'closed'];
  const parentOf = (oid: string) => spec.members.find(m => m.oid === oid)?.parentOid ?? spec.rootOid;
  let n = 0;

  const add = (kind: TicketKind, title: string, related: Pick<Ticket, 'relatedType' | 'relatedOid' | 'personOid'>, overrides: Partial<Ticket> = {}): Ticket => {
    n++;
    const trace = kind === 'sale';
    const assigneeOid = overrides.assigneeOid ?? rng.pick(assignable);
    const overdue = !trace && rng.chance(0.25);
    const dueDate = trace ? null : addDays(today, overdue ? -rng.int(1, 12) : rng.int(0, 20));
    const ticket: Ticket = {
      oid: `tck-${spec.idp}${pad(n, 4)}`, number: `TK-${spec.code}${pad(n, 4)}`, kind, category: trace ? 'trace' : 'task', title,
      status: rng.pick(statuses.slice(0, 3)), assigneeOid, creatorOid: rng.pick(assignable), ownerOid: assigneeOid,
      dueDate, escalated: false, ...related, lastActivityDate: addDays(today, -rng.int(0, 15)), ...overrides,
    };
    if (ticket.dueDate && ticket.dueDate < today && ticket.status !== 'closed' && ticket.status !== 'resolved') {
      ticket.escalated = true;
      ticket.assigneeOid = parentOf(ticket.assigneeOid);
    }
    tickets.push(ticket);
    return ticket;
  };

  const salePolicies = db.policies.filter(p => p.origin === 'platform').slice(0, spec.tickets.sales);
  salePolicies.forEach(p => add('sale', `Venta ${p.number}`, { relatedType: 'policy', relatedOid: p.oid, personOid: p.personOid }, { status: 'closed' }));
  db.claims.filter(c => ['reported', 'documentation', 'in-analysis'].includes(c.status)).forEach(c =>
    add('claim', `Siniestro ${c.number}`, { relatedType: 'claim', relatedOid: c.oid, personOid: c.personOid }, { assigneeOid: c.ownerOid }));
  db.policies.filter(p => p.divergent).forEach(p =>
    add('reconciliation', `Conciliar póliza ${p.number}`, { relatedType: 'policy', relatedOid: p.oid, personOid: p.personOid }));
  claimSettlementOids.forEach(oid => {
    const period = db.settlements.find(x => x.oid === oid)?.period ?? '';
    add('commission-claim', `Reclamo de comisión ${period}`, { relatedType: 'settlement', relatedOid: oid, personOid: null }, { assigneeOid: spec.rootOid });
  });
  for (let i = 0; i < spec.tickets.endorsements; i++) {
    const p = rng.pick(db.policies);
    add('endorsement', `Endoso en póliza ${p.number}`, { relatedType: 'policy', relatedOid: p.oid, personOid: p.personOid });
  }
  for (let i = 0; i < spec.tickets.cancellations; i++) {
    const p = rng.pick(db.policies);
    add('cancellation', `Anulación de póliza ${p.number}`, { relatedType: 'policy', relatedOid: p.oid, personOid: p.personOid });
  }
  const freeTitles = ['Llamar al cliente por documentación', 'Revisar convenio de comisión', 'Pedir cotización de flota', 'Actualizar datos de contacto', 'Seguimiento de renovación', 'Preparar libro de emisión', 'Responder consulta de la compañía', 'Cargar credenciales del nuevo vendedor'];
  freeTitles.slice(0, spec.tickets.free).forEach(t => add('free', t, { relatedType: null, relatedOid: null, personOid: null }));
  return tickets;
}

function makeNotifications(db: ProtoDb, today: string, spec: TreeSeedSpec): ProtoNotification[] {
  const out: ProtoNotification[] = [];
  let n = 0;
  const add = (recipientOid: string, kind: ProtoNotification['kind'], title: string, body: string, daysAgo: number, related: Pick<ProtoNotification, 'relatedType' | 'relatedOid'>) => {
    n++;
    out.push({
      oid: `ntf-${spec.idp}${pad(n, 4)}`, recipientOid, kind, title, body, createDate: addDays(today, -daysAgo),
      read: daysAgo > 6, ...related,
    });
  };
  const renewals = db.policies.filter(p => p.status === 'active' && p.endDate >= today && p.endDate <= addDays(today, 30));
  add(spec.rootOid, 'renewal', `${renewals.length} pólizas renuevan en los próximos 30 días`, 'Revisalas y recotizá el mercado habilitado.', 0, { relatedType: null, relatedOid: null });
  db.installments.filter(i => i.status === 'overdue').slice(0, 6).forEach((i, idx) => {
    const policy = db.policies.find(p => p.oid === i.policyOid)!;
    add(policy.ownerOid, 'overdue-installment', `Cuota ${i.number} vencida — Póliza ${policy.number}`, 'Podés avisarle al cliente con una plantilla.', idx + 1, { relatedType: 'policy', relatedOid: policy.oid });
  });
  db.claims.filter(c => c.status === 'reported' || c.status === 'documentation').slice(0, 2).forEach((c, idx) => {
    add(c.ownerOid, 'claim-deadline', `Faltan ${5 - idx} días para el pronunciamiento — Siniestro ${c.number}`, 'Pasado el plazo de 30 días, el silencio de la compañía importa aceptación.', idx, { relatedType: 'claim', relatedOid: c.oid });
  });
  db.quotes.filter(q => q.status === 'chosen-by-client').forEach((q, idx) => {
    add(q.ownerOid, 'client-choice', `${db.persons.find(p => p.oid === q.personOid)?.displayName} eligió una opción`, `Cotización ${q.number}: todavía no lo contactaste.`, idx + 1, { relatedType: 'quote', relatedOid: q.oid });
  });
  db.tickets.filter(t => t.escalated).slice(0, 3).forEach((t, idx) => {
    add(t.assigneeOid, 'ticket-escalated', `Ticket escalado: ${t.number}`, t.title, idx + 2, { relatedType: null, relatedOid: null });
  });
  add(spec.rootOid, 'system', 'Bienvenido al prototipo', 'Los datos son ficticios y se pierden al recargar.', 8, { relatedType: null, relatedOid: null });
  return out;
}

function generateTree(today: string, spec: TreeSeedSpec): ProtoDb {
  const rng = new Rng(spec.seed);
  const persons = makePersons(rng, today, spec);
  const policies = makePolicies(rng, today, persons, spec);
  const installments = makeInstallments(rng, today, policies);
  const quotes = makeQuotes(rng, today, persons, spec);
  const claims = makeClaims(rng, today, policies, spec);
  const { settlements, claimSettlementOids } = makeSettlements(rng, today, policies, spec);

  const db: ProtoDb = {
    trees: [{ ...spec.treeInfo }],
    members: spec.members.map(m => ({ ...m })),
    companies: COMPANIES.map(c => ({ ...c, capabilities: { ...c.capabilities } })),
    persons, policies, quotes, claims, installments, settlements,
    commissionLines: [], agreements: [], splits: [], credentials: [], invitations: [],
    tickets: [], notifications: [], issuanceRequests: [], endorsements: [], documents: [], communications: [],
    auditLog: [], supportAccesses: [], catalogReviews: [], accountSettings: {}, quoteSettings: {},
    hiddenProducts: [], importRuns: [], documentShares: [], complianceFiles: [], unusualReports: [], emissionBooks: [], drafts: [],
  };
  db.tickets = makeTickets(rng, today, db, claimSettlementOids, spec);
  db.notifications = makeNotifications(db, today, spec);
  enrichLifecycle(db, today, spec.idp);
  enrichCommissions(db, today, spec.idp);
  return db;
}

const JOINED_COLLECTIONS = [
  'members', 'persons', 'policies', 'quotes', 'claims', 'installments', 'settlements', 'commissionLines',
  'agreements', 'splits', 'credentials', 'invitations', 'tickets', 'notifications', 'issuanceRequests',
  'endorsements', 'documents', 'communications',
] as const;

/** One database, several trees: each tree is generated on its own (own seed, own oids) and the collections are joined. */
export function generateSeed(today: string): ProtoDb {
  const db = generateTree(today, RIOS_SPEC);
  const federal = generateTree(today, FEDERAL_SPEC);
  for (const key of JOINED_COLLECTIONS) (db[key] as unknown[]).push(...federal[key]);
  db.trees.push(...federal.trees);
  enrichPlatform(db, today);
  return db;
}
